import { SessionError, SessionErrorKey } from './error.mjs';
const decodeBase64 = (value) => {
    // browser
    if (typeof window !== 'undefined' && window.atob) {
        return window.atob(value);
    }
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const buffer = (global === null || global === void 0 ? void 0 : global.Buffer) || require('buffer/').Buffer;
    // node.js
    return buffer.from(value, 'base64').toString('binary');
};
/**
 * Decodes a string containing a Base64URL encoded value.
 *
 * Base64URL is a form of Base64 encoding that results in a value
 * that is safe to embed in URL's.
 *
 * See: https://tools.ietf.org/html/rfc4648#section-5
 */
const decodeBase64URL = (value) => decodeBase64(value.replace(/-/g, '+').replace(/_/g, '/'));
/**
 * Encodes the specified character in hexadecimal.
 */
const encodeAsURIHex = (rune) => {
    const utf16Code = rune.charCodeAt(0);
    const hexCode = utf16Code.toString(16);
    return `%${hexCode}`;
};
/**
 * Decode unicode characters embedded in the specified value.
 */
const decodeUnicode = (value) => {
    const encodedValue = value.split('').map(encodeAsURIHex).join('');
    return decodeURIComponent(encodedValue);
};
/**
 * Extracts the payload from the specified JWT without
 * verifying the signature.
 */
export const extractJWTPayload = (jwt) => {
    const parts = jwt.split('.');
    if (parts.length < 2) {
        throw new SessionError('JWT does not have enough parts.', {
            name: SessionErrorKey.MALFORMED_JWT,
            extra: {
                token: jwt,
            },
        });
    }
    const [, payload] = parts;
    try {
        const jsonPayload = decodeBase64URL(payload);
        return JSON.parse(decodeUnicode(jsonPayload));
    }
    catch (error) {
        throw new SessionError('JWT payload cannot be extracted.', {
            name: SessionErrorKey.MALFORMED_JWT,
            cause: error,
            extra: {
                token: jwt,
            },
        });
    }
};
/**
 * Gets whether the specified JWT is reasonably valid.
 *
 * This function returning true does not mean the
 * JWT's signature is valid or that it's not expired.
 */
export const isParsableJWT = (jwt) => {
    if (!jwt) {
        return false;
    }
    try {
        return !!extractJWTPayload(jwt);
    }
    catch (_error) {
        return false;
    }
};
/**
 * Gets whether the contents of the specified JWT are expired.
 *
 * If the specified JWT does not have an expiry time,
 * this will always return false.
 */
export const isJWTPayloadExpired = (clock, payload) => {
    if (!payload.exp) {
        return false;
    }
    return clock.unixEpochNow() > payload.exp;
};
/**
 * Gets whether the specified JWT is expired.
 *
 * If the specified JWT does not have an expiry time,
 * this will always return false.
 */
export const isJWTExpired = (clock, jwt) => {
    const payload = extractJWTPayload(jwt);
    return isJWTPayloadExpired(clock, payload);
};
